import React, { useEffect, useRef, useState } from 'react';
import { VIDEO_URL_WIDE, VIDEO_URL_SQUARE } from '../data/mock';

/**
 * BackgroundVideo — sits fixed behind the entire page, full viewport.
 * Desktop: scroll position over the WHOLE document drives video.currentTime.
 * Mobile / reduced-motion: autoplay loop (fixed background still animates).
 */
const BackgroundVideo = () => {
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const targetTimeRef = useRef(0);
  const [reduced, setReduced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mob = window.matchMedia('(max-width: 768px)');
    setReduced(m.matches);
    setIsMobile(mob.matches);
    const a = (e) => setReduced(e.matches);
    const b = (e) => setIsMobile(e.matches);
    m.addEventListener('change', a);
    mob.addEventListener('change', b);
    return () => { m.removeEventListener('change', a); mob.removeEventListener('change', b); };
  }, []);

  // Prime: play briefly to decode frames so seeking shows frames cleanly
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const prime = async () => {
      try {
        await v.play();
        if (!(reduced || isMobile)) {
          // Desktop: pause after priming so scroll-scrubbing takes over.
          setTimeout(() => {
            try {
              v.pause();
              v.currentTime = 0;
            } catch (seekErr) {
              if (process.env.NODE_ENV !== 'production') console.debug('[BackgroundVideo] pause/seek failed:', seekErr);
            }
          }, 400);
        }
      } catch (playErr) {
        // Autoplay was blocked (common on Safari without user gesture). Mobile useEffect retries on touch.
        if (process.env.NODE_ENV !== 'production') console.debug('[BackgroundVideo] autoplay blocked:', playErr);
      }
    };
    const onCanPlay = () => prime();
    v.addEventListener('canplay', onCanPlay, { once: true });
    if (v.readyState >= 3) prime();
    return () => v.removeEventListener('canplay', onCanPlay);
  }, [reduced, isMobile]);

  // Mobile / reduced: keep nudging play() — iOS Safari sometimes pauses on load
  useEffect(() => {
    if (!(reduced || isMobile)) return;
    const v = videoRef.current;
    if (!v) return;
    v.loop = true;
    const tryPlay = () => v.play().catch((err) => {
      if (process.env.NODE_ENV !== 'production') console.debug('[BackgroundVideo] play() rejected:', err);
    });
    tryPlay();
    const onTouch = () => { tryPlay(); document.removeEventListener('touchstart', onTouch); document.removeEventListener('click', onTouch); };
    document.addEventListener('touchstart', onTouch, { once: true, passive: true });
    document.addEventListener('click', onTouch, { once: true });
    const onVis = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('touchstart', onTouch);
      document.removeEventListener('click', onTouch);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduced, isMobile]);

  // Desktop: scrub video timeline using whole-page scroll progress
  useEffect(() => {
    if (reduced || isMobile) return;
    const v = videoRef.current;
    if (!v) return;

    const tick = () => {
      if (v.duration && !isNaN(v.duration)) {
        const cur = v.currentTime;
        const target = targetTimeRef.current;
        const next = cur + (target - cur) * 0.12;
        try { if (Math.abs(target - cur) > 0.015) v.currentTime = next; } catch (seekErr) {
          if (process.env.NODE_ENV !== 'production') console.debug('[BackgroundVideo] scrub seek failed:', seekErr);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onScroll = () => {
      const max = (document.documentElement.scrollHeight || document.body.scrollHeight) - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (v.duration) targetTimeRef.current = Math.min(v.duration - 0.1, Math.max(0.05, p * (v.duration - 0.1)));
    };

    onScroll();
    rafRef.current = requestAnimationFrame(tick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduced, isMobile]);

  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <video
        ref={videoRef}
        muted
        playsInline
        // eslint-disable-next-line react/no-unknown-property
        webkit-playsinline="true"
        x5-playsinline="true"
        preload="auto"
        autoPlay
        loop={reduced || isMobile}
        disablePictureInPicture
        disableRemotePlayback
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={VIDEO_URL_SQUARE} type="video/mp4" media="(max-aspect-ratio: 1/1)" />
        <source src={VIDEO_URL_WIDE} type="video/mp4" />
      </video>
      {/* Subtle global scrim so content is always legible while the video keeps animating beneath. */}
      <div className="absolute inset-0 bg-[#07120F]/35" />
      {/* Vignette to focus eye to center */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 0%, rgba(10,10,11,0.35) 60%, rgba(10,10,11,0.85) 100%)' }} />
    </div>
  );
};

export default BackgroundVideo;
